import { Chain } from '@1delta/chain-registry'
import { Lender } from '@1delta/lender-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import { LenderAssetReward, RewardSourceRef } from '../types'
import { RewardFetchOutput, RewardFetcher, RewardResult } from './types'

/**
 * dTRINITY dLEND borrow-rebate reader.
 *
 * dLEND is an unmodified Aave v3.0.x fork, so every other layer of the stack
 * treats it as stock Aave. Its ONE economic departure is the reason this file
 * exists: dTRINITY subsidises **borrowers** out of dUSD float revenue, and it
 * does so with a plain Aave incentive emission attached to the **variable debt
 * token**. At integration (2026-08-06) that made the Ethereum net borrow rate
 * NEGATIVE — 5.65 % raw against a 5.77 % rebate. A dLEND row that reports only
 * the Aave rate is therefore not merely incomplete, it has the sign wrong.
 *
 * Two traps are baked into the shape of this reader:
 *
 *  1. **The incentives controller must be read off the DEBT token.** The
 *     aToken's `getIncentivesController()` returns the zero address on
 *     Ethereum; only the variable debt token points at the live proxy
 *     (`0x04f07d28…`). Equally, the deployment artifact named
 *     `RewardsController` (`0x06f0c640…`) answers `getRewardsList()` with an
 *     EMPTY array — it is not the contract holding the rebate state. Resolving
 *     the controller per-token from the chain sidesteps both.
 *  2. **`distributionEnd` is a hard cliff.** Both live emissions expire
 *     (2026-12-31 Ethereum, 2026-10-10 Fraxtal). A cached rate that ignores it
 *     keeps advertising a negative borrow cost after the subsidy stops, so the
 *     APR is zeroed the moment the deadline passes.
 *
 * See DTRINITY.md for the full assessment.
 */

/** dLEND `Pool` proxies we read. Ethereum only — see DTRINITY.md §7 for
 * why Sonic (every reserve frozen) and Fraxtal (chain 252 is not wired into
 * `chainMapping.ts`) are deliberately absent. */
const DLEND_POOLS: { chainId: string; lender: string; pool: string }[] = [
  {
    chainId: Chain.ETHEREUM_MAINNET,
    lender: Lender.DTRINITY,
    pool: '0x6598DaD18Bda89A0E58A1F427c8CeBc0dE90F153',
  },
]

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
const SECONDS_PER_YEAR = 31_536_000

/**
 * One merged ABI across the Pool, AddressesProvider, AaveOracle, the incentives
 * controller and the ERC-20 surface of the reserve tokens. `multicallRetry*`
 * takes a single ABI per batch and viem resolves fragments by name, so the
 * names must stay unique — they are.
 */
const ABI = [
  {
    name: 'getReservesList',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address[]' }],
  },
  {
    name: 'ADDRESSES_PROVIDER',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
  {
    name: 'getPriceOracle',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
  {
    name: 'getAssetPrice',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'getReserveData',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [
      {
        type: 'tuple',
        components: [
          {
            name: 'configuration',
            type: 'tuple',
            components: [{ name: 'data', type: 'uint256' }],
          },
          { name: 'liquidityIndex', type: 'uint128' },
          { name: 'currentLiquidityRate', type: 'uint128' },
          { name: 'variableBorrowIndex', type: 'uint128' },
          { name: 'currentVariableBorrowRate', type: 'uint128' },
          { name: 'currentStableBorrowRate', type: 'uint128' },
          { name: 'lastUpdateTimestamp', type: 'uint40' },
          { name: 'id', type: 'uint16' },
          { name: 'aTokenAddress', type: 'address' },
          { name: 'stableDebtTokenAddress', type: 'address' },
          { name: 'variableDebtTokenAddress', type: 'address' },
          { name: 'interestRateStrategyAddress', type: 'address' },
          { name: 'accruedToTreasury', type: 'uint128' },
          { name: 'unbacked', type: 'uint128' },
          { name: 'isolationModeTotalDebt', type: 'uint128' },
        ],
      },
    ],
  },
  {
    name: 'getIncentivesController',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
  {
    name: 'getRewardsByAsset',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'address[]' }],
  },
  {
    name: 'getRewardsData',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }, { type: 'address' }],
    outputs: [
      { name: 'index', type: 'uint256' },
      { name: 'emissionPerSecond', type: 'uint256' },
      { name: 'lastUpdateTimestamp', type: 'uint256' },
      { name: 'distributionEnd', type: 'uint256' },
    ],
  },
  {
    name: 'totalSupply',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'decimals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint8' }],
  },
  {
    name: 'symbol',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'string' }],
  },
] as const

/**
 * dTRINITY pays the rebate itself, out of dUSD float revenue — there is no
 * campaign platform in the middle, so the source is the protocol.
 */
const DTRINITY_SOURCE: RewardSourceRef = {
  id: 'dtrinity:rebate',
  label: 'dTRINITY borrow rebate',
  link: 'https://docs.dtrinity.org/protocol-components/dlend',
  platform: 'protocol',
}

const isZero = (a: string | undefined) => !a || a.toLowerCase() === ZERO_ADDRESS

const scale = (raw: bigint, decimals: number) => Number(raw) / 10 ** decimals

function emptyReward(): LenderAssetReward {
  return {
    distribution: 'onchain-incentives',
    deposit: 0,
    borrow: 0,
    additionalDepositData: [],
    additionalBorrowData: [],
  }
}

/**
 * A single (reserve token, incentivised balance token) leg — an aToken means a
 * supply-side reward, a variable debt token means a borrow-side rebate.
 */
interface Leg {
  /** underlying reserve address, lowercased — the key the reward map uses */
  asset: string
  /** aToken or variableDebtToken carrying the emission */
  balanceToken: string
  bucket: 'deposit' | 'borrow'
}

/**
 * Convert an emission to a percentage APR against the incentivised balance.
 *
 * Aave distributes pro-rata to scaled balances, so a holder's share of the
 * emission equals their share of the token's total — which makes
 * `emissionPerYear / totalSupply` the per-unit rate directly, both legs priced
 * through the pool's own oracle so a rebate paid in some OTHER token (a
 * governance change away) still converts correctly rather than silently
 * reporting the wrong number.
 */
export function rebateApr(params: {
  emissionPerSecond: bigint
  distributionEnd: bigint
  rewardDecimals: number
  rewardPrice: number
  balanceTotalSupply: bigint
  balanceDecimals: number
  assetPrice: number
  nowSeconds: number
}): number {
  const {
    emissionPerSecond,
    distributionEnd,
    rewardDecimals,
    rewardPrice,
    balanceTotalSupply,
    balanceDecimals,
    assetPrice,
    nowSeconds,
  } = params
  // Hard cliff: past `distributionEnd` the emission stops entirely. Reporting
  // the last-known rate here is how a cached negative borrow rate outlives its
  // subsidy.
  if (nowSeconds >= Number(distributionEnd)) return 0
  if (emissionPerSecond <= 0n || balanceTotalSupply <= 0n) return 0
  if (!(rewardPrice > 0) || !(assetPrice > 0)) return 0

  const rewardsPerYearUsd =
    scale(emissionPerSecond, rewardDecimals) * SECONDS_PER_YEAR * rewardPrice
  const baseUsd = scale(balanceTotalSupply, balanceDecimals) * assetPrice
  if (!(baseUsd > 0)) return 0
  return (rewardsPerYearUsd / baseUsd) * 100
}

async function fetchForPool(
  chainId: string,
  lender: string,
  pool: string,
  nowSeconds: number,
): Promise<RewardResult> {
  const call = (address: string, name: string, params: any[] = []) => ({
    address,
    name,
    params,
  })

  // Round 1 — reserve roster + the addresses provider (for the oracle).
  const [reservesList, addressesProvider] = (await multicallRetryUniversal({
    chain: chainId,
    abi: ABI,
    calls: [call(pool, 'getReservesList'), call(pool, 'ADDRESSES_PROVIDER')],
    allowFailure: false,
  })) as [string[], string]

  if (!reservesList?.length) return {}

  // Round 2 — the price oracle plus every reserve's token triplet.
  const round2 = (await multicallRetryUniversal({
    chain: chainId,
    abi: ABI,
    calls: [
      call(addressesProvider, 'getPriceOracle'),
      ...reservesList.map((a) => call(pool, 'getReserveData', [a])),
    ],
    allowFailure: false,
  })) as any[]

  const oracle = round2[0] as string
  const reserveData = round2.slice(1)

  // Both legs of every reserve. The aToken's controller reads as the zero
  // address on Ethereum today, so the supply legs drop out on their own — but
  // querying them costs one call each and means a future lender-side incentive
  // is picked up without a code change.
  const legs: Leg[] = []
  reservesList.forEach((asset, i) => {
    const rd = reserveData[i]
    if (!rd) return
    const a = asset.toLowerCase()
    if (!isZero(rd.aTokenAddress))
      legs.push({ asset: a, balanceToken: rd.aTokenAddress, bucket: 'deposit' })
    if (!isZero(rd.variableDebtTokenAddress))
      legs.push({
        asset: a,
        balanceToken: rd.variableDebtTokenAddress,
        bucket: 'borrow',
      })
  })
  if (!legs.length) return {}

  // Round 3 — per-leg controller + balance totals, and the underlying prices.
  const round3 = (await multicallRetryUniversal({
    chain: chainId,
    abi: ABI,
    calls: [
      ...legs.flatMap((l) => [
        call(l.balanceToken, 'getIncentivesController'),
        call(l.balanceToken, 'totalSupply'),
        call(l.balanceToken, 'decimals'),
      ]),
      ...reservesList.map((a) => call(oracle, 'getAssetPrice', [a])),
    ],
    allowFailure: false,
  })) as any[]

  const legState = legs.map((l, i) => ({
    ...l,
    controller: round3[i * 3] as string,
    totalSupply: round3[i * 3 + 1] as bigint,
    decimals: Number(round3[i * 3 + 2]),
  }))
  const priceOffset = legs.length * 3
  const priceByAsset: Record<string, number> = {}
  reservesList.forEach((a, i) => {
    // The AaveOracle's base unit is 1e8 on every dLEND deployment. Prices only
    // ever appear here as a ratio (reward vs asset), so the unit cancels and
    // the raw integer is safe to use directly.
    priceByAsset[a.toLowerCase()] = Number(round3[priceOffset + i] ?? 0n)
  })

  const incentivised = legState.filter((l) => !isZero(l.controller))
  if (!incentivised.length) return {}

  // Round 4 — which reward tokens each incentivised leg pays.
  const rewardLists = (await multicallRetryUniversal({
    chain: chainId,
    abi: ABI,
    calls: incentivised.map((l) =>
      call(l.controller, 'getRewardsByAsset', [l.balanceToken]),
    ),
    allowFailure: false,
  })) as string[][]

  const pairs = incentivised.flatMap((l, i) =>
    (rewardLists[i] ?? []).map((reward) => ({ leg: l, reward })),
  )
  if (!pairs.length) return {}

  // Round 5 — emission state, and the reward token's own decimals/price. A
  // reward token that the pool's oracle cannot price yields 0 rather than a
  // fabricated rate.
  const round5 = (await multicallRetryUniversal({
    chain: chainId,
    abi: ABI,
    calls: pairs.flatMap((p) => [
      call(p.leg.controller, 'getRewardsData', [p.leg.balanceToken, p.reward]),
      call(p.reward, 'decimals'),
      call(oracle, 'getAssetPrice', [p.reward]),
      call(p.reward, 'symbol'),
    ]),
    allowFailure: false,
  })) as any[]
  const STRIDE = 4

  const result: RewardResult = {}
  pairs.forEach((p, i) => {
    const data = round5[i * STRIDE] as any[]
    if (!data) return
    const rewardDecimals = Number(round5[i * STRIDE + 1] ?? 18)
    const rewardPrice = Number(round5[i * STRIDE + 2] ?? 0n)
    const rewardSymbol = round5[i * STRIDE + 3] as string | undefined
    const distributionEnd = Number(data[3] ?? 0)
    const apr = rebateApr({
      emissionPerSecond: BigInt(data[1] ?? 0),
      distributionEnd: BigInt(data[3] ?? 0),
      rewardDecimals,
      rewardPrice,
      balanceTotalSupply: BigInt(p.leg.totalSupply ?? 0),
      balanceDecimals: p.leg.decimals,
      assetPrice: priceByAsset[p.leg.asset] ?? 0,
      nowSeconds,
    })
    if (!(apr > 0)) return

    if (!result[chainId]) result[chainId] = {}
    if (!result[chainId][lender]) result[chainId][lender] = {}
    if (!result[chainId][lender][p.leg.asset])
      result[chainId][lender][p.leg.asset] = emptyReward()
    const entry = result[chainId][lender][p.leg.asset]

    const breakdown = {
      tokenAddress: p.reward.toLowerCase(),
      emissionPerSecond: String(data[1]),
      distributionEnd,
      distributionType: 'aave-incentives',
    }

    // Typed stream alongside the legacy bag. `claim: 'accrual'` because this is
    // a first-class Aave emission the borrower claims from the incentives
    // controller — no off-chain merkle proof, unlike every Merkl campaign.
    if (!entry.streams) entry.streams = []
    entry.streams.push({
      side: p.leg.bucket,
      apr,
      kind: 'token',
      token: {
        address: p.reward.toLowerCase(),
        chainId,
        symbol: rewardSymbol,
        decimals: rewardDecimals,
        // The pool's AaveOracle is 1e8-based on every dLEND deployment.
        priceUsd: rewardPrice > 0 ? rewardPrice / 1e8 : undefined,
      },
      source: {
        ...DTRINITY_SOURCE,
        refs: {
          incentivesController: p.leg.controller.toLowerCase(),
          balanceToken: p.leg.balanceToken.toLowerCase(),
          emissionPerSecond: String(data[1]),
        },
      },
      claim: 'accrual',
      endsAt: distributionEnd > 0 ? distributionEnd : undefined,
      dailyRewardsUsd:
        rewardPrice > 0
          ? (Number(data[1] ?? 0) / 10 ** rewardDecimals) *
            86_400 *
            (rewardPrice / 1e8)
          : undefined,
    })

    if (p.leg.bucket === 'deposit') {
      entry.deposit += apr
      entry.additionalDepositData.push(breakdown)
    } else {
      entry.borrow += apr
      entry.additionalBorrowData.push(breakdown)
    }
  })

  return result
}

/**
 * Reads the live rebate straight off the chain — there is no Merkl campaign
 * and no dTRINITY API behind this, the emission IS the product.
 */
export const dtrinityRebateRewardFetcher: RewardFetcher = {
  label: 'DTRINITY_REBATE',
  // On-chain source: the pool (and therefore the lender key) is known up front,
  // so this stays lender-keyed — no deployment ambiguity to resolve.
  fetch: async (): Promise<RewardFetchOutput> => {
    const nowSeconds = Math.floor(Date.now() / 1000)
    const results = await Promise.all(
      DLEND_POOLS.map(async ({ chainId, lender, pool }) => {
        try {
          return await fetchForPool(chainId, lender, pool, nowSeconds)
        } catch (e) {
          console.error(`dTRINITY: rebate read failed on chain ${chainId}:`, e)
          return {} as RewardResult
        }
      }),
    )

    const merged: RewardResult = {}
    for (const r of results) {
      for (const [chainId, lenders] of Object.entries(r)) {
        if (!merged[chainId]) merged[chainId] = {}
        for (const [lender, assets] of Object.entries(lenders)) {
          if (!merged[chainId][lender]) merged[chainId][lender] = {}
          Object.assign(merged[chainId][lender], assets)
        }
      }
    }
    return { byLender: merged }
  },
}
